import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { FoodItemWithKey } from '@shared/services';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useMealPlanMutations } from '@web-app/hooks/useMealPlanMutations';
import { useMolecule } from 'bunshi/react';
import debounce from 'debounce';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { PiPencilSimple } from 'react-icons/pi';

import { EditorV2 } from '../EditorV2/EditorV2';
import { EditorName } from '../TipTap';
import { Text } from '../Typography/Text';
import { MACROS, MacroType, MealType } from './utils';

const MACRO_LABELS: Record<MacroType, string> = {
  calories: 'Calories',
  carbohydrates: 'Carbs',
  protein: 'Protein',
  fat: 'Fat',
};

interface MealDetailProps {
  meal?: FoodItemWithKey;
  day: number;
  mealType: MealType;
  readOnly: boolean;
  showNutritionInfo?: boolean;
}
export const MealDetail = ({
  meal,
  day,
  mealType,
  readOnly,
  showNutritionInfo = false,
}: MealDetailProps) => {
  const {
    editDetailedMealPlanAtom,
    detailedMealPlanEditModeAtom,
    analyticsContextAtom,
  } = useMolecule(mealPlanMolecule);
  const [editDetailedMealPlan, setEditDetailedMealPlan] = useAtom(
    editDetailedMealPlanAtom,
  );
  const [detailedMealPlanEditMode, setDetailedMealPlanEditMode] = useAtom(
    detailedMealPlanEditModeAtom,
  );
  const analyticsContext = useAtomValue(analyticsContextAtom);
  const { saveDetailedMealPlanEdit } = useMealPlanMutations();

  const key = useRef<string>();

  const { trackEvent } = useAnalytics();
  const editAlreadyRun = useRef(false);
  const initChange = useRef(false);
  const dayMealTypeRef = useRef({ day, mealType });

  useEffect(
    function syncEditStateFromMeal() {
      if (meal?.key !== key.current) {
        setEditDetailedMealPlan({
          day,
          mealType,
          editorContent: meal?.body || '',
          macroData: MACROS.reduce((acc, macroKey) => {
            return { ...acc, [macroKey]: meal?.[macroKey] ?? 0 };
          }, {}),
        });
        key.current = meal?.key;
      }
    },
    [day, meal, mealType, setEditDetailedMealPlan],
  );

  useEffect(
    function resetTrackingRefsOnMealChange() {
      if (
        dayMealTypeRef.current.day !== day ||
        dayMealTypeRef.current.mealType !== mealType
      ) {
        dayMealTypeRef.current = { day, mealType };
        initChange.current = false;
        editAlreadyRun.current = false;
      }
    },
    [day, mealType],
  );

  const trackDetailsChange = useCallback(() => {
    if (!initChange.current) {
      initChange.current = true;
      return;
    }
    if (!editAlreadyRun.current) {
      trackEvent(EVENT_NAMES.EDITS_MEAL_RECIPE, { context: analyticsContext });
      editAlreadyRun.current = true;
    }
  }, [trackEvent, analyticsContext]);

  const onCancel = () => {
    setDetailedMealPlanEditMode(false);
    setEditDetailedMealPlan({
      day,
      mealType,
      editorContent: meal?.body || '',
      macroData: MACROS.reduce((acc, key) => {
        return { ...acc, [key]: meal?.[key] ?? 0 };
      }, {}),
    });
  };

  const updateMacroData = (key: string, value: number) => {
    setEditDetailedMealPlan({
      day,
      mealType,
      editorContent: editDetailedMealPlan?.editorContent || '',
      macroData: {
        ...editDetailedMealPlan?.macroData,
        [key]: value,
      },
    });
  };

  const setEditDetailedMealPlanDebounced = useRef(
    debounce(setEditDetailedMealPlan, 200),
  );

  const updateEditor = (value: string) => {
    const isContextSynced =
      editDetailedMealPlan?.day === day &&
      editDetailedMealPlan?.mealType === mealType;

    if (editDetailedMealPlan?.macroData && isContextSynced) {
      setEditDetailedMealPlanDebounced.current({
        day,
        mealType,
        editorContent: value,
        macroData: editDetailedMealPlan.macroData,
      });
    }
  };

  const onSaveHandler = async () => {
    await saveDetailedMealPlanEdit();
  };

  const editorKey = detailedMealPlanEditMode ? 'editable' : 'readonly';

  return (
    <Flex flexDirection="column" gap={4}>
      <Flex
        flexDirection="column"
        id={`${meal}-${day}`}
        className="section"
        data-day={day}
        data-meal={mealType}
      >
        <Flex justifyContent="space-between" alignItems="center" mt={6} mb={6}>
          <Text textType="lg" fontWeight={600}>
            {meal?.title}
          </Text>
          {!readOnly && !detailedMealPlanEditMode && (
            <Button
              variant="outline"
              size="sm"
              flexShrink={0}
              leftIcon={<Icon as={PiPencilSimple} />}
              onClick={() => setDetailedMealPlanEditMode(true)}
            >
              Edit recipe
            </Button>
          )}
          {!readOnly && detailedMealPlanEditMode && (
            <HStack spacing={2} flexShrink={0}>
              <Button variant="ghost" size="sm" onClick={onCancel}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="green" onClick={onSaveHandler}>
                Save recipe
              </Button>
            </HStack>
          )}
        </Flex>

        {showNutritionInfo && (
          <HStack spacing={14} mt={4} mb={6}>
            {MACROS.map((macro: MacroType) => (
              <Box key={`macro-input-${macro}`}>
                {!detailedMealPlanEditMode &&
                  !!editDetailedMealPlan?.macroData &&
                  !!editDetailedMealPlan.macroData[macro] && (
                    <>
                      <Text textType="xs" fontWeight={600}>
                        {MACRO_LABELS[macro]}
                      </Text>
                      <Text marginTop="8px" textType="sm">
                        {editDetailedMealPlan.macroData[macro]}
                        {macro === 'calories' ? '' : 'g'}
                      </Text>
                    </>
                  )}
                {detailedMealPlanEditMode && (
                  <>
                    <Text textType="xs" fontWeight={600}>
                      {MACRO_LABELS[macro]}
                    </Text>
                    <InputGroup marginTop={2} maxWidth="120px">
                      <Input
                        backgroundColor="white"
                        type="number"
                        key={`${macro}-input`}
                        value={
                          editDetailedMealPlan?.macroData
                            ? editDetailedMealPlan?.macroData[macro]
                            : 0
                        }
                        onChange={(e) => {
                          updateMacroData(macro, parseFloat(e.target.value));
                        }}
                        borderColor="green.300"
                        borderRadius={8}
                        py={1.5}
                        px={3}
                      />
                      {macro !== 'calories' && (
                        <InputRightElement>
                          <Text textType="xs" opacity={0.5}>
                            g
                          </Text>
                        </InputRightElement>
                      )}
                    </InputGroup>
                  </>
                )}
              </Box>
            ))}
          </HStack>
        )}

        <EditorV2
          key={editorKey}
          name={EditorName.MealPlanDetail}
          value={
            editDetailedMealPlan?.day === day &&
            editDetailedMealPlan?.mealType === mealType
              ? editDetailedMealPlan.editorContent
              : meal?.body || ''
          }
          onChange={(html: string) => {
            trackDetailsChange();
            updateEditor(html);
          }}
          readOnly={!detailedMealPlanEditMode}
          showToolbar={false}
          showBorder={detailedMealPlanEditMode}
        />
      </Flex>
    </Flex>
  );
};
